import { useNavigate } from 'react-router-dom';
import {
  deliveryAssigneeLabel,
  deliveryStatusTone,
  deliveryTypeLabel,
  formatDeliveryBatchAccountsLabel,
  formatDeliveryMoney,
  formatDeliveryStatusLabel,
  type DeliveryBatchAccount,
} from '../../lib/deliveryArrangements';
import { formatSingaporeDate, formatSingaporeDateTime } from '../../utils/dateTime';
import type { DeliveryArrangement } from '../../types/delivery';

type DeliveryListRowProps = {
  row: DeliveryArrangement;
  accounts?: DeliveryBatchAccount[];
  detailPath: string;
};

export function DeliveryListRow({ row, accounts, detailPath }: DeliveryListRowProps) {
  const navigate = useNavigate();
  const typeLabel = deliveryTypeLabel(row.fulfillment_type);
  const assignee = deliveryAssigneeLabel(row);
  const isLalamove = row.fulfillment_type === 'lalamove';
  const stopAccounts =
    accounts && accounts.length > 0
      ? accounts
      : [
          {
            name: row.client_name || row.dem_customer_code || '—',
            code: row.dem_customer_code?.trim() || '',
            reason: row.reason?.trim() || '',
          },
        ];
  const accountLabel = formatDeliveryBatchAccountsLabel(stopAccounts);
  const createdAt = formatSingaporeDate(row.created_at);
  const createdAtTitle = formatSingaporeDateTime(row.created_at);
  const creator = row.created_by?.trim() || '—';
  const statusLabel = formatDeliveryStatusLabel(row.status);
  const statusTone = deliveryStatusTone(row.status);
  // Lalamove: show actual fee when known; Internal stays on reference quote.
  const feeAmount =
    isLalamove && row.fee_amount != null && Number.isFinite(row.fee_amount)
      ? row.fee_amount
      : row.reference_fee_amount;

  const openDetails = () => {
    navigate(detailPath);
  };

  return (
    <div
      className="delivery-list-row"
      role="link"
      tabIndex={0}
      onClick={openDetails}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDetails();
        }
      }}
    >
      <span className="delivery-list-row__created-cell" title={createdAtTitle}>
        <span className="delivery-list-row__created">{createdAt}</span>
        <span className={`delivery-list-row__status delivery-list-row__status--${statusTone}`}>
          {statusLabel}
        </span>
      </span>

      <span className="delivery-list-row__type-cell">
        <span className={`delivery-list-row__type delivery-list-row__type--${row.fulfillment_type}`}>
          {typeLabel}
        </span>
        {!isLalamove ? (
          <span className="delivery-list-row__assignee" title={assignee}>
            {assignee}
          </span>
        ) : null}
      </span>

      <span className="delivery-list-row__stops" title={accountLabel}>
        <span className="delivery-list-row__stops-head" aria-hidden>
          <span>Account</span>
          <span>Reason</span>
        </span>
        {stopAccounts.map((account, index) => {
          const name = account.name?.trim() || '—';
          const code = account.code?.trim() || '';
          const reason = account.reason?.trim() || '—';
          return (
            <span key={`${code || name}-${index}`} className="delivery-list-row__stop-line">
              <span className="delivery-list-row__stop-account">
                {code && code !== name ? (
                  <span className="delivery-list-row__stop-code mono">{code}</span>
                ) : null}
                <span className="delivery-list-row__stop-name">{name}</span>
              </span>
              <span className="delivery-list-row__stop-reason" title={reason}>
                {reason}
              </span>
            </span>
          );
        })}
      </span>

      <span className="delivery-list-row__fee">
        {formatDeliveryMoney(feeAmount, row.reference_fee_currency)}
      </span>

      <span className="delivery-list-row__text delivery-list-row__creator" title={creator}>
        {creator}
      </span>

      <span
        className="delivery-list-row__text delivery-list-row__requested"
        title={row.requested_by?.trim() || undefined}
      >
        {row.requested_by?.trim() || '—'}
      </span>

      <span
        className="delivery-list-row__text delivery-list-row__issue"
        title={row.issue_caused_by?.trim() || undefined}
      >
        {row.issue_caused_by?.trim() || '—'}
      </span>
    </div>
  );
}
